import React from "react";
import { t } from "../../i18n";
import { ChatPanel } from "./ChatPanel";
import { useResizablePanel } from "./useResizablePanel";
import "./AiChatDrawer.scss";

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Docked container for the AI chat, sitting between the sidebar and the page content. The layout
 * shift (push on desktop, overlay / full-width sheet on smaller widths) is driven entirely by CSS
 * keyed on the shell wrapper's `chat-panel-open` class, so this component only renders chrome.
 *
 * The right edge is drag-resizable (push tier only) via `useResizablePanel`, which writes the width
 * to the `--ai-chat-width` CSS variable the panel and content margin both derive from.
 *
 * `ChatPanel` stays mounted while closed — the drawer hides via `visibility` (see the SCSS), which
 * keeps it out of the tab order and the a11y tree yet preserves the transcript and any in-flight
 * request when the drawer is peeked shut and reopened.
 */
export const AiChatDrawer = ({ open, onClose }: Props) => {
  const resizeHandlers = useResizablePanel();
  return (
    <>
      <div
        className={"ai-chat-drawer__backdrop" + (open ? " ai-chat-drawer__backdrop--visible" : "")}
        onClick={onClose}
      />
      <aside
        className={"ai-chat-drawer" + (open ? " ai-chat-drawer--open" : "")}
        data-testid="ai-chat-drawer"
      >
        <div className="ai-chat-drawer__header">
          <div className="ai-chat-drawer__title">
            {t("ai-chat.panel-title", undefined, "AI Assistant")}
            <small>{t("ai-chat.subtitle", undefined, "Grounded in live platform data")}</small>
          </div>
          <button
            type="button"
            className="ai-chat-drawer__close"
            onClick={onClose}
            aria-label={t("ai-chat.close", undefined, "Close AI chat")}
            data-testid="ai-chat-close"
          >
            <i className="fe-x" />
          </button>
        </div>
        <div className="ai-chat-drawer__body">
          <ChatPanel />
        </div>
        <div
          className="ai-chat-drawer__resize"
          role="separator"
          aria-orientation="vertical"
          aria-label={t("ai-chat.resize", undefined, "Resize AI chat panel")}
          data-testid="ai-chat-resize"
          {...resizeHandlers}
        />
      </aside>
    </>
  );
};
